import React, { useState } from 'react';
import pricingConfig from '../config/pricing.json';

const Pricing: React.FC = () => {
  const [location, setLocation] = useState<'india' | 'uae'>('india');
  const [activeCategory, setActiveCategory] = useState<'withoutAds' | 'withAds'>('withoutAds');
  const [showComparison, setShowComparison] = useState(false);

  const { currencies, categories, comparisonFeatures } = pricingConfig;
  const currentCurrency = currencies[location];
  const currentPlans = categories[activeCategory].plans;

  const formatPrice = (price: number) => {
    return location === 'india' ? `${currentCurrency.symbol}${price.toLocaleString()}` : `${currentCurrency.symbol} ${price.toLocaleString()}`;
  };

  const renderFeatureValue = (value: any) => {
    if (typeof value === 'boolean') {
      return value ? <i className="fas fa-check text-green"></i> : <i className="fas fa-times text-red"></i>;
    }
    return value;
  };

  return (
    <section id="pricing" className="pricing">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Choose Your Plan</h2>
          <p className="section-description">
            Flexible pricing options to suit your business needs
          </p>
        </div>

        <div className="pricing-controls">
          <div className="location-toggle">
            <button
              className={location === 'india' ? 'active' : ''}
              onClick={() => setLocation('india')}
            >
              <i className="fas fa-rupee-sign"></i>
              {currencies.india.name}
            </button>
            <button
              className={location === 'uae' ? 'active' : ''}
              onClick={() => setLocation('uae')}
            >
              <i className="fas fa-coins"></i>
              {currencies.uae.name}
            </button>
          </div>

          <div className="category-toggle">
            <button
              className={activeCategory === 'withoutAds' ? 'active' : ''}
              onClick={() => setActiveCategory('withoutAds')}
            >
              {categories.withoutAds.title}
            </button>
            <button
              className={activeCategory === 'withAds' ? 'active' : ''}
              onClick={() => setActiveCategory('withAds')}
            >
              {categories.withAds.title}
            </button>
          </div>
        </div>

        <div className="pricing-grid">
          {currentPlans.map((plan) => (
            <div key={plan.id} className={`pricing-card ${plan.isPopular ? 'popular' : ''}`}>
              {plan.isPopular && <div className="popular-badge">Most Popular</div>}
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <p>{plan.description}</p>
                <div className="plan-price">
                  <span className="price">{formatPrice(plan.price[location])}</span>
                  <span className="period">/month</span>
                </div>
                {plan.setupCost[location] > 0 && (
                  <div className="setup-cost">
                    Setup: {formatPrice(plan.setupCost[location])}
                  </div>
                )}
              </div>
              <div className="plan-features">
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <i className="fas fa-check"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="plan-footer">
                <button className="plan-button">
                  Choose {plan.name}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="pricing-footer">
          <button
            className="comparison-button"
            onClick={() => setShowComparison(true)}
          >
            <i className="fas fa-table"></i>
            Compare All Plans
          </button>
        </div>

        {showComparison && (
          <div className="comparison-modal">
            <div className="comparison-content">
              <div className="comparison-header">
                <h3>Plan Comparison</h3>
                <button
                  className="close-button"
                  onClick={() => setShowComparison(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="comparison-table">
                <table>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Gold</th>
                      <th>Silver</th>
                      <th>Platinum</th>
                      <th>Gold with Ads</th>
                      <th>Silver with Ads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((category) => (
                      <React.Fragment key={category.category}>
                        <tr className="category-row">
                          <td colSpan={6}>{category.category}</td>
                        </tr>
                        {category.features.map((feature) => (
                          <tr key={feature.name}>
                            <td>{feature.name}</td>
                            <td>{renderFeatureValue(feature.gold)}</td>
                            <td>{renderFeatureValue(feature.silver)}</td>
                            <td>{renderFeatureValue(feature.platinum)}</td>
                            <td>{renderFeatureValue(feature.gold_ads)}</td>
                            <td>{renderFeatureValue(feature.silver_ads)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Pricing; 